import { formatTimeAgo } from "@/shared/utils/helperFunctions";
import AIPill from "@/shared/components/ui/AiPill/AIPill";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import NewsScoreTooltipContent from "@/shared/components/ui/Upload/ToolTip/NewsScoreTooltipContent";
import {
  CONTENT_LINK_CLASS,
  resolveContentLink,
} from "@/shared/utils/contentLinkUtils";
import { Info } from "lucide-react";

const SequenceNewsCard = ({
  news,
  expanded,
  onToggleSelect,
  selected,
  newsSources,
  showNewsScore = true,
  hideBottomBorder = false,
}) => {
  // Handle news as string
  const isString = typeof news === "string";
  let newsDate = "";
  let newsTitle = "";
  let displayDate = "";
  let parsedDateForAgo = "";
  let extractedSource = null;

  const cleanSummaryText = (textStr) => {
    if (!textStr || typeof textStr !== "string") return "";
    return textStr
      .replace(
        /\s*\(\s*\(\s*\[[^\]]+\]\(https?:\/\/[^\s)]+\)\s*\)\s*(?:\[[^\]]+\])?\s*\)/gi,
        "",
      )
      .replace(
        /\s*\(\s*\[[^\]]+\]\(https?:\/\/[^\s)]+\)\s*\)(?:\[[^\]]+\])?/gi,
        "",
      )
      .replace(/\s*\[[^\]]+\]\(https?:\/\/[^\s)]+\)/gi, "")
      .replace(/\s*\(Source:[^)]+\)/gi, "")
      .replace(
        /\s*\([a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(\/[^)]*)?\)/gi,
        "",
      )
      .replace(/\s*\[[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}\]/gi, "")
      .trim();
  };

  if (isString) {
    // Extract source
    let sourceMatch = news.match(/\(Source:\s*([^)]+)\)/);
    let sourceText = null;
    if (sourceMatch) {
      sourceText = sourceMatch[1].trim();
    } else {
      const markdownMatch =
        news.match(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/) ||
        news.match(/\((https?:\/\/[^\s)]+)\)/);
      if (markdownMatch) {
        sourceText = markdownMatch[2] || markdownMatch[1];
      } else {
        const domainMatch = news.match(
          /\(([a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(\/[^)]*)?)\)/,
        );
        if (domainMatch) {
          sourceText = domainMatch[1].trim();
        }
      }
    }

    if (sourceText) {
      extractedSource = resolveContentLink(sourceText);
    }

    // Extract date
    const datePatterns = [
      /^([A-Za-z]+\s+\d{1,2}(?:[–-]\d{1,2})?,\s+\d{4})/, // "July 1, 2025"
      /^([A-Za-z]{3}\s+\d{1,2}(?:[–-]\d{1,2})?,\s+\d{4})/, // "Jul 1, 2025"
    ];

    for (const pattern of datePatterns) {
      const dateMatch = news.match(pattern);
      if (dateMatch) {
        newsDate = dateMatch[1];
        displayDate = newsDate;

        // Parse date for formatTimeAgo
        const firstDateMatch = newsDate.match(/^([A-Za-z]+)\s+(\d{1,2})/);
        if (firstDateMatch) {
          const [, month, day] = firstDateMatch;
          const yearMatch = newsDate.match(/(\d{4})/);
          const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
          parsedDateForAgo = `${month} ${day}, ${year}`;
        }
        break;
      }
    }

    // remove links and sources
    newsTitle = cleanSummaryText(news?.article_summary || news);

    // Remove date prefix
    if (displayDate) {
      newsTitle = newsTitle
        .replace(
          new RegExp(
            `^${displayDate.replace(
              /[.*+?^${}()|[\]\\]/g,
              "\\$&",
            )}\\s*(?:\\(announced[^)]+\\))?\\s*:?\\s*`,
            "i",
          ),
          "",
        )
        .trim();
    }
  } else {
    // news is an object: { date, article_summary, source, news_score, score_components, ... }
    const rawSummary = news.article_summary ?? "";
    newsTitle = cleanSummaryText(rawSummary);

    if (news.date) {
      const d = new Date(news.date);
      if (!isNaN(d.getTime())) {
        parsedDateForAgo = d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        displayDate = parsedDateForAgo;
      }
    }

    if (news.source) {
      extractedSource = resolveContentLink(news.source);
    } else if (rawSummary) {
      const urlMatch =
        rawSummary.match(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/) ||
        rawSummary.match(/(https?:\/\/[^\s)]+)/);
      if (urlMatch) {
        extractedSource = resolveContentLink(urlMatch[2] || urlMatch[1]);
      }
    }
  }

  const isAI = !isString && (news.ai || news.is_ai_suggested || false);

  const sourceLabel = newsSources
    ? `${newsSources.charAt(0).toUpperCase()}${newsSources.slice(1)}`
    : extractedSource?.displayText
      ? `${extractedSource.displayText.charAt(0).toUpperCase()}${extractedSource.displayText.slice(1)}`
      : null;

  const timeAgoLabel = parsedDateForAgo
    ? (() => {
        const ago = formatTimeAgo(parsedDateForAgo);
        return ago !== "Invalid date" ? ago : null;
      })()
    : null;

  const newsScoreValue =
    !isString && typeof news?.news_score === "number"
      ? news.news_score.toFixed(1)
      : null;

  const showMetaScore = newsScoreValue != null && !showNewsScore;

  const scoreTooltipContent = news?.score_components ? (
    <NewsScoreTooltipContent
      scoreComponents={news.score_components}
      newsScore={!isString ? news?.news_score : null}
    />
  ) : null;

  return (
    <div
      className={`p-4 flex gap-4 items-center ${
        expanded && !hideBottomBorder ? "border-b border-gray-200" : ""
      }`}
    >
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {extractedSource?.url ? (
            <p className="max-w-[140px] truncate text-[10px] font-semibold text-gray-500 dark:text-gray-400">
              {sourceLabel ?? "View source"}
            </p>
          ) : (
            <span className="max-w-[140px] truncate text-[10px] font-semibold text-gray-600 dark:text-gray-400">
              {sourceLabel ?? "Unknown source"}
            </span>
          )}

          <span className="text-gray-300 select-none" aria-hidden="true">
            ·
          </span>

          <time
            dateTime={parsedDateForAgo || undefined}
            className="shrink-0 text-[10px] font-medium text-gray-500 dark:text-gray-400"
          >
            {timeAgoLabel ?? "Date unavailable"}
          </time>

          {showMetaScore && (
            <>
              <span className="text-gray-300 select-none" aria-hidden="true">
                ·
              </span>
              <CustomCommonTooltip
                content={scoreTooltipContent}
                className="max-w-[320px]"
                placement="top-start"
              >
                <span className="inline-flex cursor-default items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                  Score {newsScoreValue}
                  {news?.score_components ? (
                    <Info size={11} strokeWidth={2} aria-hidden="true" />
                  ) : null}
                </span>
              </CustomCommonTooltip>
            </>
          )}
        </div>
        {newsTitle && (
          <p className="text-xs text-gray-800 dark:text-gray-200 font-normal text-wrap break-words">
            {newsTitle}
            {extractedSource ? (
              <a
                href={extractedSource.url || "#"}
                target={extractedSource.url ? "_blank" : undefined}
                rel={extractedSource.url ? "noopener noreferrer" : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!extractedSource.url) e.preventDefault();
                }}
                className={`${CONTENT_LINK_CLASS} ml-1`}
              >
                [{extractedSource.displayText}]
              </a>
            ) : null}
          </p>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          {showNewsScore && !isString && news.news_score != null && (
            <CustomCommonTooltip
              content={scoreTooltipContent}
              className="max-w-[320px]"
              placement="top-start"
            >
              <div className="py-0.5 px-2 items-center justify-center flex rounded-2xl bg-secondary-transparent w-fit h-fit cursor-pointer">
                <span className="text-[10.5px] text-secondary font-medium">
                  Score:{" "}
                  {typeof news.news_score === "number"
                    ? news.news_score.toFixed(1)
                    : news.news_score}
                </span>
              </div>
            </CustomCommonTooltip>
          )}
          {isAI && <AIPill text="AI Suggested" />}
        </div>
      </div>
    </div>
  );
};

export default SequenceNewsCard;
